/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ExpertContent,
  ExpertUploadMediaParam,
  IMediaUpdate,
  MediaLibraryState,
  MediaResponse,
  RemainingStorage,
  SaveDownloadVideoTrimmedParams,
  Sort,
} from './interface';

export const initialState: MediaLibraryState = {
  loading: false,
  modalLoading: false,
  filters: {
    keyword: '',
    type: [],
    sortBy: Sort.UPLOADED,
  },
  count: 0,
  page: 0,
  perPage: 12,
  filtersApplied: false,
  path: '/root',
  selectedFiles: [],
  media: [],
  total: 0,
  stroage: {
    allowedStorage: 0,
    mediaStorageSize: 0,
    remainingStorage: 0,
  },
};

export const mediaLibrarySlice = createSlice({
  name: '/media-library',
  initialState,
  reducers: {
    setCount: (state, action: PayloadAction<number>) => {
      state.count = action.payload;
    },
    setPath: (state, action: PayloadAction<string>) => {
      state.path = action.payload;
    },
    startLoading: (state) => {
      state.loading = true;
    },
    endLoading: (state) => {
      state.loading = false;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.perPage = action.payload;
    },
    setKeyword: (state, action: PayloadAction<string>) => {
      state.page = 0;
      state.filters.keyword = action.payload;

      mediaLibrarySlice.caseReducers.postUpdate(state);
    },
    setSortBy: (state, action: PayloadAction<Sort>) => {
      state.page = 0;
      state.filters.sortBy = action.payload;

      mediaLibrarySlice.caseReducers.postUpdate(state);
    },
    setType: (state, action: PayloadAction<string>) => {
      state.page = 0;

      if (state.filters.type.includes(action.payload)) {
        state.filters.type = state.filters.type.filter((i) => i !== action.payload);
      } else {
        state.filters.type = [...state.filters.type, action.payload];
      }

      mediaLibrarySlice.caseReducers.postUpdate(state);
    },
    clearFilters: (state) => {
      state.page = 0;
      state.filters = {
        keyword: '',
        type: [],
        sortBy: Sort.UPLOADED,
      };

      mediaLibrarySlice.caseReducers.postUpdate(state);
    },
    fetchMedia(state: MediaLibraryState) {
      state.loading = true;
    },
    fetchMediaSucceed(state: MediaLibraryState, action: PayloadAction<MediaResponse>) {
      state.loading = false;
      state.media = action.payload.result;
      state.total = action.payload.total;
    },
    fetchMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    fetchRemainingStroage(state: MediaLibraryState) {
      state.loading = true;
    },
    fetchRemainingStroageFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    fetchRemainingStroageSucceed(
      state: MediaLibraryState,
      action: PayloadAction<RemainingStorage>,
    ) {
      const stroage = action.payload;
      state.loading = false;
      state.stroage = stroage;
    },
    uploadMedia(state: MediaLibraryState, _action: PayloadAction<ExpertUploadMediaParam>) {
      state.loading = true;
    },
    uploadMediaSucceed(state: MediaLibraryState, _action: PayloadAction<ExpertContent>) {
      state.loading = false;
    },
    uploadMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    deleteMedia(
      state: MediaLibraryState,
      action: PayloadAction<{ id: string; contentUrl: string | null; fileSize: number }>,
    ) {
      state.loading = true;
    },
    deleteMediaSucceed(state: MediaLibraryState, _action: PayloadAction<ExpertContent>) {
      state.loading = false;
    },
    deleteMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    deleteSelectedMedia(state: MediaLibraryState) {
      state.loading = true;
    },
    deleteSelectedMediaSucceed(state: MediaLibraryState) {
      state.loading = false;
    },
    deleteSelectedMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    selectAllFiles: (state) => {
      state.selectedFiles = state.media.map((i) => i._id);
    },
    deselectAllFiles: (state) => {
      state.selectedFiles = [];
    },
    toggleSelectedFile: (state, action: PayloadAction<string>) => {
      if (state.selectedFiles.includes(action.payload)) {
        state.selectedFiles = state.selectedFiles.filter((i) => i !== action.payload);
      } else {
        state.selectedFiles = [...state.selectedFiles, action.payload];
      }
    },
    postUpdate: (state) => {
      let filtersApplied = false;

      if (state.filters.keyword.length > 0) {
        filtersApplied = true;
      }

      if (state.filters.type.length > 0) {
        filtersApplied = true;
      }

      if (state.filters.sortBy !== Sort.AZ) {
        filtersApplied = true;
      }

      state.filtersApplied = filtersApplied;
    },
    updateMedia(state: MediaLibraryState, _action: PayloadAction<IMediaUpdate>) {
      state.loading = true;
    },
    updateMediaSucceed(state: MediaLibraryState, _action: PayloadAction<IMediaUpdate>) {
      state.loading = false;
    },
    updateMediaFailed(state: MediaLibraryState) {
      state.loading = false;
    },
    saveDownloadTrimmedVideo(
      state: MediaLibraryState,
      _action: PayloadAction<SaveDownloadVideoTrimmedParams>,
    ) {
      state.modalLoading = true;
    },
    saveDownloadTrimmedVideoSuccess(state: MediaLibraryState) {
      state.modalLoading = false;
    },
    saveDownloadTrimmedVideoFailed(state: MediaLibraryState) {
      state.modalLoading = false;
    },
  },
});

export const { actions: mediaLibraryAction } = mediaLibrarySlice;
